import React from "react";
import { useNavigate } from "react-router-dom";
import { MdSave, MdDelete, MdImage } from "react-icons/md";
import { useAppLanguage } from "hooks/useAppLanguage";
import { useCourse } from "../../context/CourseContext";
import FileUploader, { type UploadedFile } from "components/ui/FileUploader";
import ConfirmDialog from "components/ui/ConfirmDialog";
import {
  instructorCoursesService,
  buildPricingPayload,
  resolveCoursePricingType,
  type CourseUpsertBody,
  type CoursePricingType,
} from "services/instructorCoursesService";
import { instructorAuthService, type InstructorAuthUser } from "services/instructorAuthService";
import BasicAcademicAssignmentSelects from "../../components/BasicAcademicAssignmentSelects";
import CoursePricingFields from "../../components/CoursePricingFields";
import { toastError, toastSuccess } from "lib/toast";
import { resolveStoredUploadPreview } from "lib/storedUploadImage";

export default function SettingsPage() {
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const navigate = useNavigate();
  const { course, reload } = useCourse();

  const [form, setForm] = React.useState<CourseUpsertBody>({
    title: course.title,
    description: course.description ?? null,
    image: course.image ?? null,
    subject_id: Number(course.subject_id ?? 0),
  });
  const [profile, setProfile] = React.useState<InstructorAuthUser | null>(null);
  const courseSubject = course.subject as any;
  const savedGradeId = course.grade_id ?? courseSubject?.grade_id ?? null;
  const savedStageId = course.grade?.stage_id ?? courseSubject?.stage_id ?? null;
  const [stageId, setStageId] = React.useState<number | null>(
    savedStageId ? Number(savedStageId) : null
  );
  const [gradeId, setGradeId] = React.useState<number | null>(
    savedGradeId !== null && savedGradeId !== undefined ? Number(savedGradeId) : null
  );
  const [saving, setSaving] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [pricingType, setPricingType] = React.useState<CoursePricingType>(() =>
    resolveCoursePricingType(course)
  );
  const [price, setPrice] = React.useState<string>(() => {
    const p = Number(course.price ?? 0);
    return resolveCoursePricingType(course) === "paid" && p > 0 ? String(p) : "";
  });
  const [priceError, setPriceError] = React.useState<string | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = React.useState<string | null>(() => {
    const img = resolveStoredUploadPreview(course.image, course.image_url);
    return img || null;
  });

  const assignedStages = profile?.stages ?? [];
  const assignedGrades = profile?.grades ?? [];
  const assignedSubjects = profile?.subjects ?? [];

  React.useEffect(() => {
    (async () => {
      try {
        const instructorProfile = await instructorAuthService.getProfile();
        setProfile(instructorProfile ?? null);
      } catch {
        // silent
      }
    })();
  }, []);

  const handleImageChange = (files: UploadedFile[]) => {
    const done = files.find((f) => f.status === "done" && f.key);
    if (done?.key) {
      setForm((p) => ({ ...p, image: done.key }));
      setImagePreviewUrl(done.localPreviewUrl || done.url || null);
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toastError(isArabic ? "العنوان مطلوب" : "Title is required");
      return;
    }
    if (!stageId) {
      toastError(isArabic ? "المرحلة مطلوبة" : "Stage is required");
      return;
    }
    if (!gradeId) {
      toastError(isArabic ? "الصف مطلوب" : "Grade is required");
      return;
    }
    if (!form.subject_id) {
      toastError(isArabic ? "المادة مطلوبة" : "Material is required");
      return;
    }
    if (pricingType === "paid" && !(Number(price) > 0)) {
      setPriceError(isArabic ? "أدخل سعرًا أكبر من صفر" : "Enter a price greater than 0");
      toastError(isArabic ? "سعر الكورس مطلوب" : "Course price is required");
      return;
    }
    setPriceError(null);
    setSaving(true);
    try {
      await instructorCoursesService.update(course.id, {
        title: form.title.trim(),
        description: form.description?.trim() || null,
        image: form.image || null,
        subject_id: Number(form.subject_id),
        grade_id: gradeId,
        ...buildPricingPayload({ pricingType, price }),
      });
      await reload();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    await instructorCoursesService.remove(course.id);
    toastSuccess(isArabic ? "تم حذف الكورس" : "Course deleted");
    navigate("/instructor/courses");
  };

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h2 className="text-lg font-bold text-gray-800 dark:text-white">
          {isArabic ? "إعدادات الكورس" : "Course settings"}
        </h2>
        <p className="text-sm text-gray-500">
          {isArabic ? "عدّل بيانات الكورس والصورة." : "Edit course details and image."}
        </p>
      </header>

      <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-navy-800">
        <div className="grid gap-4 md:grid-cols-2">
          <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 md:col-span-2">
            {isArabic ? "العنوان *" : "Title *"}
            <input
              value={form.title}
              onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
              className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
            />
          </label>
          <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 md:col-span-2">
            {isArabic ? "الوصف" : "Description"}
            <textarea
              value={form.description ?? ""}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              rows={4}
              className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
            />
          </label>
          <div className="md:col-span-2">
            <h2 className="text-sm font-bold text-navy-700 dark:text-white">
              {isArabic ? "التعيين الأكاديمي" : "Academic assignment"}
            </h2>
            <p className="text-xs text-gray-500">
              {isArabic
                ? "اختر المسار الأكاديمي ثم المادة من تعييناتك."
                : "Choose the academic path, then the material from your assignments."}
            </p>
          </div>
          <BasicAcademicAssignmentSelects
            isArabic={isArabic}
            stages={assignedStages}
            grades={assignedGrades}
            subjects={assignedSubjects}
            stageId={stageId}
            gradeId={gradeId}
            subjectId={form.subject_id || null}
            onStageChange={(id) => {
              setStageId(id);
              setGradeId(null);
              setForm((p) => ({ ...p, subject_id: 0 }));
            }}
            onGradeChange={(id) => {
              setGradeId(id);
              setForm((p) => ({ ...p, subject_id: 0 }));
            }}
            onSubjectChange={(id) => setForm((p) => ({ ...p, subject_id: id ?? 0 }))}
          />
          <CoursePricingFields
            isArabic={isArabic}
            pricingType={pricingType}
            price={price}
            onPricingTypeChange={(next) => {
              setPricingType(next);
              if (next === "free") {
                setPrice("");
                setPriceError(null);
              }
            }}
            onPriceChange={(next) => {
              setPrice(next);
              if (priceError) setPriceError(null);
            }}
            priceError={priceError}
          />
          <div className="md:col-span-2">
            <p className="text-xs font-semibold text-gray-600 dark:text-gray-300">
              {isArabic ? "صورة الكورس" : "Course image"}
            </p>
            <div className="mt-1 flex flex-col gap-3 md:flex-row">
              <div className="flex h-28 w-full items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-gray-50 text-gray-400 dark:border-white/10 dark:bg-white/5 md:w-44">
                {imagePreviewUrl ? (
                  <img
                    src={imagePreviewUrl}
                    alt={isArabic ? "صورة الكورس" : "Course image"}
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      // If the stored value isn't a real image URL, fall back to placeholder.
                      (e.currentTarget as HTMLImageElement).style.display = "none";
                      setImagePreviewUrl(null);
                    }}
                  />
                ) : (
                  <MdImage className="h-8 w-8" />
                )}
              </div>
              <FileUploader
                group="documents"
                accept="image/*"
                multiple={false}
                onChange={handleImageChange}
                className="flex-1"
                label=""
                hint={isArabic ? "مسموح بصور فقط" : "Images only"}
              />
            </div>
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
          >
            <MdSave className="h-5 w-5" />
            {saving ? (isArabic ? "..." : "...") : isArabic ? "حفظ التغييرات" : "Save changes"}
          </button>
        </div>
      </section>

      <section className="rounded-2xl border border-red-200 bg-red-50 p-5 dark:border-red-500/30 dark:bg-red-500/10">
        <h3 className="text-sm font-bold text-red-700 dark:text-red-300">
          {isArabic ? "منطقة الخطر" : "Danger zone"}
        </h3>
        <p className="mt-1 text-xs text-red-700/80 dark:text-red-200/80">
          {isArabic
            ? "حذف الكورس يحذف كل الإعلانات والمجلدات والعناصر والاختبارات."
            : "Deleting this course removes all announcements, folders, items and quizzes."}
        </p>
        <button
          type="button"
          onClick={() => setDeleteOpen(true)}
          className="mt-3 inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
        >
          <MdDelete className="h-5 w-5" />
          {isArabic ? "حذف الكورس" : "Delete course"}
        </button>
      </section>

      <ConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        title={isArabic ? "حذف الكورس" : "Delete course"}
        description={
          isArabic
            ? `سيتم حذف "${course.title}" وكل محتواه. لا يمكن التراجع.`
            : `"${course.title}" and all its content will be deleted. This cannot be undone.`
        }
        confirmLabel={isArabic ? "نعم، احذف" : "Yes, delete"}
      />
    </div>
  );
}
